import * as React from "react";

import type {
    CellClassName,
    ColumnBuilder,
    ColumnOptions,
    FormattedView,
    SimpleColumn,
    Layout,
    Display,
} from "@impero-com/common/components/dataTable";
import { toColumn } from "@impero-com/common/components/dataTable";
import { Toolbar, ToolBarTheme } from "@impero-com/common/components/toolbar";
import exportData from "@impero-com/common/img/export.svg";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { ExportMetadata } from "@impero-com/common/utils/export";
import { set, clone, assertNever } from "@impero-com/common/utils/obj";
import { toSet } from "@impero-com/common/utils/set";

import { MultiTree, MultiTreeProps } from "components/multiTree";
import { Column } from "components/table";
import {
    TableWithToolBar,
    ToolSpec,
    DefaultTableWithToolBarStyles,
} from "components/tableWithToolBar";
import blackSettings from "img/dataViewer/settings.svg";

import { ViewAdjuster, stateToIsColumnDisplayed } from "./adjuster";
import { DataViewerGroupingSelector } from "./grouping";
import {
    DataViewerSettingsDialog,
    DataViewerSettings,
    AggregatesSpec,
    AggregatesConfiguration,
    buildExtractorAggregates,
} from "./settings";
import * as theme from "./theme.css";

type AggregateColumnBuilder<ITEM, VAL> = (
    groupExtractor: DV.DataExtractor<ITEM, VAL>,
    extractor: DV.DataExtractor<ITEM, VAL>
) => SimpleColumn<ITEM>;

const DEFAULT_COLUMN_BUILDER_ID = "__DEFAULT__";

function sortColumns<ITEM>(columns: Array<SimpleColumn<ITEM>>) {
    const positions: { [colId: string]: number } = {};
    columns.forEach((col, pos) => {
        positions[col.extractor.id] = pos;
    });
    columns.sort(
        Cmp.chain([
            Cmp.map((col) => col.order, Cmp.undefinedLast()),
            Cmp.map((col) => positions[col.extractor.id]),
        ])
    );
}

class TableDataViewer<ITEM> extends React.Component<
    TableDataViewerProps<ITEM>,
    TableDataViewerState<ITEM>
> {
    constructor(props: TableDataViewerProps<ITEM>) {
        super(props);
        const formattedView = this.buildFormattedView(
            props.dataView,
            props.columnBuilders
        );
        const availableAggregates = this.getAvailableAggregates();
        const settings = TableDataViewer.getTableSettings(
            props,
            availableAggregates
        );
        const mode = this.buildMode(
            formattedView,
            props,
            settings.groupingExtractorIds,
            buildExtractorAggregates(availableAggregates, settings),
            settings.summarizeBy
        );
        this.state = {
            settings,
            dialog: undefined,
            formattedView,
            mode,
            displayedTableColumns: {},
            displayedPivotColumns: {},
        };
        this.handleRefresh(this.state.mode);
    }
    private getAvailableAggregates() {
        return this.props.availableAggregates
            ? this.props.availableAggregates
            : {
                  aggregates: [],
                  dimensions: [],
              };
    }
    // FVItem can either be ITEM or Expando<ITEM>
    private buildFormattedView<FVItem>(
        dataView: DV.DataView<FVItem>,
        columnBuilders:
            | { [extractorId: string]: ColumnBuilder<any, any> }
            | undefined
    ) {
        const allColumns = dataView.extractorGroups.reduce(
            (memo, group) => {
                return memo.concat(
                    group.extractors.map((extractor) => {
                        const builder =
                            (columnBuilders &&
                                (columnBuilders[extractor.id] ||
                                    columnBuilders[
                                        DEFAULT_COLUMN_BUILDER_ID
                                    ])) ||
                            undefined;
                        if (builder) {
                            return toColumn(extractor, builder(extractor));
                        } else {
                            return toColumn(extractor);
                        }
                    })
                );
            },
            [] as Array<SimpleColumn<FVItem>>
        );
        const displayedColumns = allColumns.filter(
            (col) => col.display !== "NEVER"
        );
        sortColumns(displayedColumns);
        const formattedView: FormattedView<FVItem> = {
            dataView,
            columns: displayedColumns,
        };
        return formattedView;
    }
    componentWillReceiveProps(nextProps: TableDataViewerProps<ITEM>): void {
        if (
            nextProps.dataView !== this.props.dataView ||
            nextProps.settings !== this.props.settings
        ) {
            const availableAggregates = this.getAvailableAggregates();
            const settings = TableDataViewer.getTableSettings(
                nextProps,
                availableAggregates
            );
            const formattedView = this.buildFormattedView(
                nextProps.dataView,
                nextProps.columnBuilders
            );
            this.setState(
                {
                    settings,
                    formattedView,
                    mode: this.buildMode(
                        formattedView,
                        nextProps,
                        settings.groupingExtractorIds,
                        buildExtractorAggregates(
                            this.getAvailableAggregates(),
                            settings
                        ),
                        settings.summarizeBy
                    ),
                },
                () => this.handleRefresh(this.state.mode)
            );
        } else if (nextProps.zoomGroupId !== this.props.zoomGroupId) {
            const mode = this.state.mode;
            if (mode.type === "PIVOT") {
                const dv = mode.formattedView.dataView;
                const zoomInNode = nextProps.zoomGroupId
                    ? findGroupNodeByItemId(
                          dv.dataSource,
                          nextProps.zoomGroupId
                      )
                    : undefined;
                if (zoomInNode) {
                    this.setState(
                        {
                            mode: set(mode, {
                                zoom: this.buildPivotZoom(
                                    this.state.formattedView,
                                    mode.formattedView.dataView,
                                    mode.pivotExtractorIds,
                                    zoomInNode
                                ),
                            }),
                        },
                        () => this.handleRefresh(this.state.mode)
                    );
                } else {
                    this.setState(
                        {
                            mode: set(mode, { zoom: undefined }),
                        },
                        () => this.handleRefresh(this.state.mode)
                    );
                }
            } else if (mode.type === "PIVOT_EXPANDED") {
                if (nextProps.zoomGroupId) {
                    const availableAggregates = this.getAvailableAggregates();
                    const settings = TableDataViewer.getTableSettings(
                        nextProps,
                        availableAggregates
                    );
                    const maybeExpandedPivotZoom =
                        this.buildMaybeExpandedPivotZoom(
                            this.state.formattedView,
                            nextProps.expanders,
                            nextProps.zoomGroupId,
                            settings.groupingExtractorIds,
                            buildExtractorAggregates(
                                availableAggregates,
                                settings
                            ),
                            settings.summarizeBy,
                            {
                                type: "Expanded",
                                viewForPivot: mode.expandedDataView,
                                aggregateExtractorGroups:
                                    mode.aggregateExtractorGroups,
                                pivotedView: mode.formattedView.dataView,
                            }
                        );
                    this.setState(
                        {
                            mode: set(mode, { zoom: maybeExpandedPivotZoom }),
                        },
                        () => this.handleRefresh(this.state.mode)
                    );
                } else {
                    this.setState(
                        {
                            mode: set(mode, { zoom: undefined }),
                        },
                        () => this.handleRefresh(this.state.mode)
                    );
                }
            }
        }
    }
    render() {
        return (
            <>
                {this.buildDialog()}
                {this.renderContent()}
            </>
        );
    }
    private buildTools(mode: ToolsMode) {
        const tools: Array<ToolSpec> = [];
        const toolDefs: Array<ToolSpec | DataViewerToolSpec> =
            (this.props.tools && this.props.tools[mode.name]) ||
            this.getDefaultToolSpecs(mode);
        toolDefs.forEach((def) => {
            if (
                (def as DataViewerSettingsToolSpec).dataViewerSettings !==
                undefined
            ) {
                tools.push({
                    type: "Image",
                    label: "",
                    image: blackSettings,
                    onCommand: () =>
                        this.setState({ dialog: { type: "Settings" } }),
                });
            } else if (
                (def as DataViewerZoomToolSpec).dataViewerZoom &&
                mode.name === "zoom"
            ) {
                tools.push({
                    type: "Icon",
                    icon: "glyphicon glyphicon-chevron-left",
                    label: "",
                    onCommand: () => {
                        if (this.props.onZoomOut) {
                            this.props.onZoomOut();
                        }
                    },
                });
                tools.push({
                    type: "Dynamic",
                    getTool: () => (
                        <div className={theme.zoomTool}>
                            {mode.pathValues.formatted}
                        </div>
                    ),
                });
            } else if (
                (def as DataViewerAdjustViewToolSpec).dataViewerAdjustView
            ) {
                tools.push({
                    type: "Dynamic",
                    getTool: () => this.buildViewAdjuster(),
                });
            } else if ((def as DataViewerExportToolSpec).dataViewerExport) {
                tools.push({
                    type: "Image",
                    label: "",
                    image: exportData,
                    onCommand: () => this.exportToExcel(),
                });
            } else {
                tools.push(def as ToolSpec);
            }
        });
        return tools;
    }

    private static getTableSettings<ITEM>(
        props: TableDataViewerProps<ITEM>,
        availableAggregates: AggregatesConfiguration<ITEM>
    ): DataViewerSettings {
        return (
            props.settings || {
                groupingExtractorIds: props.defaultGroupingExtractorIds || [],
                aggregates: props.defaultAggregates ||
                    (availableAggregates &&
                    availableAggregates.dimensions &&
                    availableAggregates.dimensions.length
                        ? {
                              type: "Dimension",
                              dimensionId: availableAggregates.dimensions[0].id,
                          }
                        : undefined) || {
                        type: "Custom",
                        aggregateIds: [],
                    },
                summarizeBy: "NONE",
            }
        );
    }

    private getDefaultToolSpecs(
        mode: ToolsMode
    ): Array<ToolSpec | DataViewerToolSpec> {
        const settingsToolDef: DataViewerSettingsToolSpec =
            this.getAvailableAggregates().dimensions.length > 0
                ? { dataViewerSettings: true }
                : { dataViewerSettings: false };
        switch (mode.name) {
            case "table":
            case "pivot":
                return [
                    // To fit the height of the zoom tools
                    { dataViewerExport: true },
                    { type: "Spacer", flex: "auto" },
                    { dataViewerAdjustView: true },
                    settingsToolDef,
                ];
            case "zoom":
                return [
                    { dataViewerZoom: true },
                    { dataViewerExport: true },
                    { type: "Spacer", flex: "auto" },
                    { dataViewerAdjustView: true },
                    settingsToolDef,
                ];
            default:
                return assertNever(mode);
        }
    }

    private buildViewAdjuster() {
        const { mode } = this.state;
        switch (mode.type) {
            case "SIMPLE":
                const { columns } = this.state.formattedView;
                return this.buildTableViewAdjuster(columns);
            case "PIVOT":
                if (mode.zoom) {
                    const { columns } = mode.zoom.formattedView;
                    return this.buildTableViewAdjuster(columns);
                } else {
                    const { columns } = mode.formattedView;
                    return this.buildPivotViewAdjuster(columns);
                }
            case "PIVOT_EXPANDED":
                if (mode.zoom) {
                    switch (mode.zoom.type) {
                        case "Simple": {
                            const { columns } = mode.zoom.zoom.formattedView;
                            return this.buildTableViewAdjuster(columns);
                        }
                        case "Expanded": {
                            const { columns } = mode.zoom.zoom.formattedView;
                            return this.buildTableViewAdjuster(columns);
                        }
                        default: {
                            return assertNever(mode.zoom);
                        }
                    }
                } else {
                    const { columns } = mode.formattedView;
                    return this.buildPivotViewAdjuster(columns);
                }
            default:
                return assertNever(mode);
        }
    }
    private buildTableViewAdjuster<T>(columns: Array<SimpleColumn<T>>) {
        return (
            <ViewAdjuster
                columns={columns}
                displayedColumns={this.state.displayedTableColumns}
                addToDisplayedColumns={(colId, displayed) => {
                    const newDisplayedColumns = clone(
                        this.state.displayedTableColumns
                    );
                    newDisplayedColumns[colId] = displayed;
                    this.setState({
                        displayedTableColumns: newDisplayedColumns,
                    });
                }}
                deleteFromDisplayedColumns={(colId) => {
                    const newDisplayedColumns = clone(
                        this.state.displayedTableColumns
                    );
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete newDisplayedColumns[colId];
                    this.setState({
                        displayedTableColumns: newDisplayedColumns,
                    });
                }}
            />
        );
    }
    private buildPivotViewAdjuster<T>(columns: Array<SimpleColumn<T>>) {
        return (
            <ViewAdjuster
                columns={columns}
                displayedColumns={this.state.displayedPivotColumns}
                addToDisplayedColumns={(colId, displayed) => {
                    const newDisplayedColumns = clone(
                        this.state.displayedPivotColumns
                    );
                    newDisplayedColumns[colId] = displayed;
                    this.setState({
                        displayedPivotColumns: newDisplayedColumns,
                    });
                }}
                deleteFromDisplayedColumns={(colId) => {
                    const newDisplayedColumns = clone(
                        this.state.displayedPivotColumns
                    );
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete newDisplayedColumns[colId];
                    this.setState({
                        displayedPivotColumns: newDisplayedColumns,
                    });
                }}
            />
        );
    }

    private renderContent() {
        const { mode } = this.state;
        switch (mode.type) {
            case "SIMPLE":
                return this.renderSimple(
                    this.props.id,
                    this.state.formattedView
                );
            case "PIVOT":
                if (mode.zoom) {
                    return this.renderZoom(this.props.id + "-zoom", mode.zoom);
                } else {
                    return this.renderPivot(mode.formattedView);
                }
            case "PIVOT_EXPANDED":
                if (mode.zoom) {
                    switch (mode.zoom.type) {
                        case "Simple": {
                            return this.renderZoom(
                                this.props.id + "-zoom",
                                mode.zoom.zoom
                            );
                        }
                        case "Expanded": {
                            return this.renderZoom(
                                this.props.id + "-zoom",
                                mode.zoom.zoom
                            );
                        }
                        default: {
                            return assertNever(mode.zoom);
                        }
                    }
                } else {
                    return this.renderPivot(mode.formattedView);
                }
            default:
                return assertNever(mode);
        }
    }

    private toStaticCellClassName = <T,>(
        cellClassName: CellClassName<T> | undefined
    ): string | undefined => {
        if (!cellClassName) {
            return undefined;
        }
        switch (cellClassName.type) {
            case "Static": {
                return cellClassName.value;
            }
            case "Dynamic": {
                throw new Error(
                    "DataViewer does not support dynamic cellClassName"
                );
            }
            default: {
                return assertNever(cellClassName);
            }
        }
    };

    private renderSimple(id: string, formattedView: FormattedView<ITEM>) {
        const { toolbarClassName, floatingToolbar, tableMaxVisibleRows } =
            this.props;
        const { dataView } = formattedView;
        let rows = DV.toArray(dataView.dataSource.iter());
        // If in a report, the report is grouped by tag categories,
        // CRs tagged with 2+ tags in the category will appear once per tag in zoom version
        // But the zoomed version contains links to the "base" item and should never have duplicates
        if (this.state.mode.type === "PIVOT_EXPANDED") {
            const seenBaseItemIds = new Set<string>();
            rows = rows.filter((row) => {
                const baseItemId = this.props.dataView.dataSource.getId(
                    (row as unknown as DV.Expando<ITEM>).baseItem
                );
                const seen = seenBaseItemIds.has(baseItemId);
                if (!seen) {
                    seenBaseItemIds.add(baseItemId);
                }
                return !seen;
            });
        }

        const isColumnDisplayed = stateToIsColumnDisplayed<ITEM>(
            this.state.displayedTableColumns
        );
        const columns: Array<Column<ITEM, React.ReactNode, any, any>> =
            formattedView.columns
                .filter((simpleCol) => isColumnDisplayed(simpleCol))
                .map((simpleCol) => {
                    const cellClassName = this.toStaticCellClassName(
                        simpleCol.cellClassName
                    );
                    const baseData = {
                        name: simpleCol.extractor.id,
                        label: simpleCol.extractor.label,
                        cellClassName,
                        getData: (row: ITEM) =>
                            simpleCol.getFormattedValue(row),
                    };
                    if (simpleCol.layout !== undefined) {
                        return {
                            ...baseData,
                            fixedWidth: simpleCol.layout.fixedWidth,
                        };
                    } else {
                        return baseData;
                    }
                });
        const sortColumn =
            dataView.sorters.length > 0
                ? dataView.sorters[0].extractorId
                : undefined;
        const orderBy =
            dataView.sorters.length > 0
                ? dataView.sorters[0].direction
                : undefined;
        return (
            <TableWithToolBar
                id={id}
                floatingToolbar={floatingToolbar}
                rows={rows}
                columns={columns}
                sortColumn={sortColumn}
                orderBy={orderBy}
                toolbarClassName={
                    toolbarClassName
                        ? toolbarClassName
                        : ToolBarTheme.reactableToolbar +
                          " " +
                          ToolBarTheme.reactableToolbarNew
                }
                maxVisibleRows={tableMaxVisibleRows}
                tools={this.buildTools({ name: "table" })}
                styles={DefaultTableWithToolBarStyles}
                sortType="external"
                onSortChanged={(extractorId, direction) => {
                    this.setState({
                        formattedView: set(this.state.formattedView, {
                            dataView: DV.sorted(
                                this.state.formattedView.dataView,
                                [{ extractorId, direction }]
                            ),
                        }),
                    });
                }}
            />
        );
    }

    // FVItem can either be ITEM or Expando<ITEM>
    private renderZoom<FVItem>(
        id: string,
        zoom: {
            pathValues: FormattedPathValues;
            formattedView: FormattedView<FVItem>;
        }
    ) {
        const { toolbarClassName, floatingToolbar, tableMaxVisibleRows } =
            this.props;
        const { dataView } = zoom.formattedView;
        const rows = DV.toArray(dataView.dataSource.iter());
        const isColumnDisplayed = stateToIsColumnDisplayed<FVItem>(
            this.state.displayedTableColumns
        );
        const columns: Array<Column<FVItem, React.ReactNode, any, any>> =
            zoom.formattedView.columns
                .filter((simpleCol) => isColumnDisplayed(simpleCol))
                .map((simpleCol) => {
                    const cellClassName = this.toStaticCellClassName(
                        simpleCol.cellClassName
                    );
                    const baseData = {
                        name: simpleCol.extractor.id,
                        label: simpleCol.extractor.label,
                        cellClassName,
                        getData: (row: FVItem) =>
                            simpleCol.getFormattedValue(row),
                    };
                    if (simpleCol.layout !== undefined) {
                        return {
                            ...baseData,
                            fixedWidth: simpleCol.layout.fixedWidth,
                        };
                    } else {
                        return baseData;
                    }
                });
        const sortColumn =
            dataView.sorters.length > 0
                ? dataView.sorters[0].extractorId
                : undefined;
        const orderBy =
            dataView.sorters.length > 0
                ? dataView.sorters[0].direction
                : undefined;
        return (
            <TableWithToolBar
                id={id}
                floatingToolbar={floatingToolbar}
                rows={rows}
                columns={columns}
                sortColumn={sortColumn}
                orderBy={orderBy}
                toolbarClassName={
                    toolbarClassName
                        ? toolbarClassName
                        : ToolBarTheme.reactableToolbar +
                          " " +
                          ToolBarTheme.reactableToolbarNew
                }
                maxVisibleRows={tableMaxVisibleRows}
                tools={this.buildTools({
                    name: "zoom",
                    pathValues: zoom.pathValues,
                })}
                styles={DefaultTableWithToolBarStyles}
                sortType="external"
                onSortChanged={(extractorId, direction): void => {
                    if (
                        this.state.mode.type === "PIVOT" &&
                        this.state.mode.zoom
                    ) {
                        this.setState({
                            mode: set(this.state.mode, {
                                zoom: set(this.state.mode.zoom, {
                                    formattedView: set(
                                        this.state.mode.zoom.formattedView,
                                        {
                                            dataView: DV.sorted(
                                                this.state.mode.zoom
                                                    .formattedView.dataView,
                                                [{ extractorId, direction }]
                                            ),
                                        }
                                    ),
                                }),
                            }),
                        });
                    } else if (
                        this.state.mode.type === "PIVOT_EXPANDED" &&
                        this.state.mode.zoom
                    ) {
                        let newZoom;
                        switch (this.state.mode.zoom.type) {
                            case "Simple": {
                                newZoom = set(this.state.mode.zoom.zoom, {
                                    formattedView: set(
                                        this.state.mode.zoom.zoom.formattedView,
                                        {
                                            dataView: DV.sorted(
                                                this.state.mode.zoom.zoom
                                                    .formattedView.dataView,
                                                [{ extractorId, direction }]
                                            ),
                                        }
                                    ),
                                });
                                break;
                            }
                            case "Expanded": {
                                newZoom = set(this.state.mode.zoom.zoom, {
                                    formattedView: set(
                                        this.state.mode.zoom.zoom.formattedView,
                                        {
                                            dataView: DV.sorted(
                                                this.state.mode.zoom.zoom
                                                    .formattedView.dataView,
                                                [{ extractorId, direction }]
                                            ),
                                        }
                                    ),
                                });
                                break;
                            }
                            default: {
                                return assertNever(this.state.mode.zoom);
                            }
                        }
                        this.setState({
                            mode: set(this.state.mode, {
                                zoom: set(this.state.mode.zoom, {
                                    zoom: newZoom,
                                }),
                            }),
                        });
                    }
                }}
            />
        );
    }

    // FVItem can either be ITEM or Expando<ITEM>
    private renderPivot<FVItem>(
        formattedView: FormattedView<DV.NodeDescriptor<DV.Group<FVItem, any>>>
    ) {
        const { dataView } = formattedView;
        const MultiTreeElt: new (
            props: MultiTreeProps<DV.Group<FVItem, any>>
        ) => React.Component<MultiTreeProps<DV.Group<FVItem, any>>, any> =
            MultiTree;
        const nodeDescs = DV.toArray(dataView.dataSource.iter());
        const children: {
            [groupId: string]:
                | Array<DV.NodeDescriptor<DV.Group<FVItem, any>>>
                | undefined;
        } = {};
        let isMultiLevel = false;
        const trees: Array<DV.NodeDescriptor<DV.Group<FVItem, any>>> = [];
        let insertPosition = 1;
        nodeDescs.forEach((nodeDesc) => {
            const level = nodeDesc.ancestors.length;
            if (level > 0) {
                const parent = nodeDesc.ancestors[level - 1];
                const existing = children[parent.id];
                if (existing === undefined) {
                    children[parent.id] = [nodeDesc];
                } else {
                    existing.push(nodeDesc);
                }
                // Don't count the root not for one level, it is displayed separately on purpose
                if (level > 1) {
                    isMultiLevel = true;
                }
            }
            if (nodeDesc.index === 0) {
                if (level !== 0) {
                    throw new Error(
                        "Node with index 0 should have no ancestors"
                    );
                }
                if (trees[0]) {
                    throw new Error("Node with index 0 duplicated");
                }
                trees[0] = nodeDesc;
            } else if (level === 1) {
                trees[insertPosition++] = nodeDesc;
            }
        });
        const sortConfiguration = {
            columnId: dataView.sorters[0].extractorId,
            direction: dataView.sorters[0].direction,
            onSortChanged: (
                extractorId: string,
                direction: DV.SortDirection
            ): void => {
                const { mode } = this.state;
                switch (mode.type) {
                    case "PIVOT": {
                        const newFormattedView = set(mode.formattedView, {
                            dataView: DV.sorted(mode.formattedView.dataView, [
                                { extractorId, direction },
                            ]),
                        });
                        this.setState({
                            mode: set(mode, {
                                formattedView: newFormattedView,
                            }),
                        });
                        break;
                    }
                    case "PIVOT_EXPANDED": {
                        const newFormattedView = set(mode.formattedView, {
                            dataView: DV.sorted(mode.formattedView.dataView, [
                                { extractorId, direction },
                            ]),
                        });
                        this.setState({
                            mode: set(mode, {
                                formattedView: newFormattedView,
                            }),
                        });
                        break;
                    }
                    case "SIMPLE":
                        console.warn(
                            "Tried to sort the pivot but we are in SIMPLE mode"
                        );
                        break;
                    default:
                        return assertNever(mode);
                }
            },
        };
        return (
            <>
                <Toolbar tools={this.buildTools({ name: "pivot" })} />
                <MultiTreeElt
                    formattedView={formattedView}
                    hideToggleWidgets={!isMultiLevel}
                    isOpenAllNode={(nodeDesc) => nodeDesc.index === 0}
                    openRoots
                    sortConfiguration={sortConfiguration}
                />
            </>
        );
    }

    private buildMode(
        formattedView: FormattedView<ITEM>,
        props: TableDataViewerProps<ITEM>,
        extractorIds: Array<string>,
        extractorAggregates: Array<DV.ExtractorAggregate<ITEM, any>>,
        summarizeBy: DV.SummarizeBy
    ): DisplayMode<ITEM> {
        if (extractorIds.length > 0) {
            return this.buildPivot(
                formattedView,
                props,
                extractorIds,
                extractorAggregates,
                summarizeBy
            );
        } else {
            return { type: "SIMPLE" };
        }
    }

    private buildPivotData(
        formattedView: FormattedView<ITEM>,
        expanders: Array<DV.ExtractorExpander<any, any>> | undefined,
        pivotExtractorIds: Array<string>,
        extractorAggregates: Array<DV.ExtractorAggregate<ITEM, any>>,
        summarizeBy: DV.SummarizeBy
    ): PivotData<ITEM> {
        const { dataView } = formattedView;
        const getId = dataView.dataSource.getId;
        if (expanders) {
            const pivotExtractorIdsSet = toSet(pivotExtractorIds);
            const expandersForPivot = expanders.filter(
                (expdr) => pivotExtractorIdsSet[expdr.extractorId] === true
            );
            if (expandersForPivot.length > 0) {
                const expandedDataView = DV.expanded(
                    dataView,
                    expandersForPivot
                );
                const expandedExtractorAggregates =
                    DV.expandedAggregates(extractorAggregates);

                const viewForPivot = expandedDataView;
                // XXX Work on typed data
                const aggregateExtractorGroups = DV.aggregate(
                    expandedExtractorAggregates
                ).buildExtractors(expandedDataView, summarizeBy, (expando) =>
                    getId(expando.baseItem)
                );
                return {
                    type: "Expanded",
                    viewForPivot,
                    aggregateExtractorGroups,
                    pivotedView: DV.pivoted(
                        viewForPivot,
                        pivotExtractorIds,
                        aggregateExtractorGroups
                    ),
                };
            } else {
                const viewForPivot = dataView;
                // XXX Work on typed data
                const aggregateExtractorGroups = DV.aggregate(
                    extractorAggregates
                ).buildExtractors(dataView, summarizeBy, getId);
                return {
                    type: "Simple",
                    viewForPivot,
                    aggregateExtractorGroups,
                    pivotedView: DV.pivoted(
                        viewForPivot,
                        pivotExtractorIds,
                        aggregateExtractorGroups
                    ),
                };
            }
        } else {
            const viewForPivot = dataView;
            // XXX Work on typed data
            const aggregateExtractorGroups = DV.aggregate(
                extractorAggregates
            ).buildExtractors(dataView, summarizeBy, getId);
            return {
                type: "Simple",
                viewForPivot,
                aggregateExtractorGroups,
                pivotedView: DV.pivoted(
                    viewForPivot,
                    pivotExtractorIds,
                    aggregateExtractorGroups
                ),
            };
        }
    }
    private buildPivot(
        formattedView: FormattedView<ITEM>,
        props: TableDataViewerProps<ITEM>,
        pivotExtractorIds: Array<string>,
        extractorAggregates: Array<DV.ExtractorAggregate<ITEM, any>>,
        summarizeBy: DV.SummarizeBy
    ) {
        function computeDisplayedColumns<ITEM>(
            pivotedView: DV.DataView<DV.NodeDescriptor<DV.Group<ITEM, any>>>,
            aggregateExtractorGroups: Array<
                DV.DataExtractorGroup<DV.NodeDescriptor<DV.Group<ITEM, any>>>
            >
        ) {
            const groupColumn = props.groupColumnBuilder
                ? toColumn(
                      pivotedView.extractorGroups[0].extractors[0],
                      props.groupColumnBuilder(
                          pivotedView.extractorGroups[0].extractors[0]
                      )
                  )
                : toColumn(pivotedView.extractorGroups[0].extractors[0], {
                      display: "ALWAYS",
                  });
            const aggregateColumns = aggregateExtractorGroups.reduce(
                (memo, group) => {
                    return memo.concat(
                        group.extractors.map((extractor) => {
                            const builder =
                                (props.aggregateColumnBuilders &&
                                    (props.aggregateColumnBuilders[
                                        extractor.id
                                    ] ||
                                        props.aggregateColumnBuilders[
                                            DEFAULT_COLUMN_BUILDER_ID
                                        ])) ||
                                undefined;
                            if (builder) {
                                return builder(
                                    pivotedView.extractorGroups[0]
                                        .extractors[0],
                                    extractor
                                );
                            } else {
                                return toColumn(extractor);
                            }
                        })
                    );
                },
                [] as Array<
                    SimpleColumn<DV.NodeDescriptor<DV.Group<ITEM, any>>>
                >
            );
            const displayedColumns = aggregateColumns.filter(
                (col) => col.display !== "NEVER"
            );
            sortColumns(displayedColumns);
            if (groupColumn.display !== "NEVER") {
                displayedColumns.splice(0, 0, groupColumn);
            }
            return displayedColumns;
        }

        const pivotData = this.buildPivotData(
            formattedView,
            props.expanders,
            pivotExtractorIds,
            extractorAggregates,
            summarizeBy
        );

        switch (pivotData.type) {
            case "Simple": {
                const { aggregateExtractorGroups, pivotedView } = pivotData;
                const displayedColumns = computeDisplayedColumns(
                    pivotedView,
                    aggregateExtractorGroups
                );
                let mode: PivotMode<ITEM> = {
                    type: "PIVOT",
                    pivotExtractorIds,
                    formattedView: {
                        dataView: pivotedView,
                        columns: displayedColumns,
                    },
                };
                if (props.zoomGroupId) {
                    const zoomInNode = findGroupNodeByItemId(
                        pivotedView.dataSource,
                        props.zoomGroupId
                    );
                    if (zoomInNode) {
                        mode = set(mode, {
                            zoom: this.buildPivotZoom(
                                formattedView,
                                pivotedView,
                                pivotExtractorIds,
                                zoomInNode
                            ),
                        });
                    }
                }
                return mode;
            }
            case "Expanded": {
                const { viewForPivot, aggregateExtractorGroups, pivotedView } =
                    pivotData;
                const displayedColumns = computeDisplayedColumns(
                    pivotedView,
                    aggregateExtractorGroups
                );
                let mode: PivotExpandedMode<ITEM> = {
                    type: "PIVOT_EXPANDED",
                    pivotExtractorIds,
                    expandedDataView: viewForPivot,
                    aggregateExtractorGroups,
                    formattedView: {
                        dataView: pivotedView,
                        columns: displayedColumns,
                    },
                };
                if (props.zoomGroupId) {
                    const maybeExpandedPivotZoom =
                        this.buildMaybeExpandedPivotZoom(
                            formattedView,
                            props.expanders,
                            props.zoomGroupId,
                            pivotExtractorIds,
                            extractorAggregates,
                            summarizeBy,
                            pivotData
                        );
                    if (maybeExpandedPivotZoom) {
                        mode = set(mode, { zoom: maybeExpandedPivotZoom });
                    }
                }
                return mode;
            }
            default: {
                return assertNever(pivotData);
            }
        }
    }
    private buildMaybeExpandedPivotZoom(
        formattedView: FormattedView<ITEM>,
        expanders: Array<DV.ExtractorExpander<any, any>> | undefined,
        zoomGroupId: string,
        pivotExtractorIds: Array<string>,
        extractorAggregates: Array<DV.ExtractorAggregate<ITEM, any>>,
        summarizeBy: DV.SummarizeBy,
        fullPivotData: PivotData<ITEM>
    ): MaybeExpandedPivotZoom<ITEM> | undefined {
        let zoomLevel: number;
        if (zoomGroupId === "/") {
            zoomLevel = 0;
        } else {
            // I am not ashamed.
            zoomLevel = zoomGroupId.match(/\//g)!.length;
        }
        const zoomGroupingExtractorIds = pivotExtractorIds.slice(0, zoomLevel);
        // Optimize a bit.
        const zoomPivotData =
            zoomGroupingExtractorIds.length === pivotExtractorIds.length
                ? fullPivotData
                : this.buildPivotData(
                      formattedView,
                      expanders,
                      zoomGroupingExtractorIds,
                      extractorAggregates,
                      summarizeBy
                  );
        switch (zoomPivotData.type) {
            case "Simple": {
                const zoomInNode = findGroupNodeByItemId(
                    zoomPivotData.pivotedView.dataSource,
                    zoomGroupId
                );
                if (zoomInNode) {
                    const zoom = this.buildPivotZoom(
                        formattedView,
                        zoomPivotData.pivotedView,
                        zoomGroupingExtractorIds,
                        zoomInNode
                    );
                    if (zoom) {
                        return {
                            type: "Simple",
                            zoom,
                        };
                    } else {
                        return undefined;
                    }
                } else {
                    return undefined;
                }
            }
            case "Expanded": {
                const zoomInNode = findGroupNodeByItemId(
                    zoomPivotData.pivotedView.dataSource,
                    zoomGroupId
                );
                if (zoomInNode) {
                    const zoom = this.buildPivotExpandedZoom(
                        formattedView,
                        zoomPivotData.viewForPivot,
                        zoomPivotData.pivotedView,
                        zoomGroupingExtractorIds,
                        zoomInNode
                    );
                    if (zoom) {
                        return {
                            type: "Expanded",
                            zoom,
                        };
                    } else {
                        return undefined;
                    }
                } else {
                    return undefined;
                }
            }
            default: {
                return assertNever(zoomPivotData);
            }
        }
    }
    private buildDialog() {
        const { dialog } = this.state;
        if (!dialog) {
            return null;
        }
        switch (dialog.type) {
            case "Settings": {
                const { dataView, defaultGroupingExtractorIds } = this.props;
                const { settings } = this.state;
                const availableAggregates = this.getAvailableAggregates();
                return (
                    <DataViewerSettingsDialog
                        extractorGroups={dataView.extractorGroups}
                        defaultGroupingExtractorIds={
                            defaultGroupingExtractorIds
                        }
                        availableAggregates={availableAggregates}
                        settings={settings}
                        onOk={(settings) => {
                            const { onSettingsChanged } = this.props;
                            const onStateUpdated = onSettingsChanged
                                ? () => {
                                      onSettingsChanged(this.state.settings);
                                  }
                                : undefined;
                            this.setState(
                                {
                                    settings,
                                    dialog: undefined,
                                    mode: this.buildMode(
                                        this.state.formattedView,
                                        this.props,
                                        settings.groupingExtractorIds,
                                        buildExtractorAggregates(
                                            availableAggregates,
                                            settings
                                        ),
                                        settings.summarizeBy
                                    ),
                                },
                                onStateUpdated
                            );
                        }}
                        onCancel={() =>
                            this.setState({
                                dialog: undefined,
                            })
                        }
                    />
                );
            }
            default:
                return null;
        }
    }
    private getPathValues<T extends ITEM | DV.Expando<ITEM>, V>(
        nodeDesc: DV.NodeDescriptor<DV.Group<T, V>>,
        getExtractorById: (extractorId: string) => DV.DataExtractor<T, any>,
        pivotExtractorIds: Array<string>
    ): FormattedPathValues {
        const item = nodeDesc.value.items[0];
        const extractorIdsForZooming = pivotExtractorIds.slice(
            0,
            nodeDesc.ancestors.length
        );
        const extAndValues: Array<ExtractorAndPathValue<T>> =
            extractorIdsForZooming.map((extractorId) => {
                const extractor = getExtractorById(extractorId);
                const value = extractor.asString(item);
                return {
                    extractor,
                    value,
                };
            });
        const pathValues = extAndValues.map((eav) => eav.value);
        const formatted = this.props.formatPathValues
            ? this.props.formatPathValues(extAndValues)
            : pathValues.join(" / ");
        return { values: pathValues, formatted };
    }
    private findNode<T>(itemId: string, dataView: DV.DataView<T>) {
        const { dataSource } = dataView;
        const iter = dataSource.iter();
        while (true) {
            const item = iter.next();
            if (item === undefined) {
                return null;
            }
            if (dataSource.getId(item) === itemId) {
                return item;
            }
        }
    }
    private buildPivotZoom(
        formattedView: FormattedView<ITEM>,
        pivotedView: DV.DataView<DV.NodeDescriptor<DV.Group<ITEM, any>>>,
        pivotExtractorIds: Array<string>,
        node: DV.NodeDescriptor<DV.Group<ITEM, any>>
    ): PivotZoom<ITEM> | undefined {
        const originalDataView = this.props.dataView;
        const zoomPathValues = this.getPathValues(
            node,
            (extractorId) => DV.getExtractor(originalDataView, extractorId),
            pivotExtractorIds
        );
        const zoomGroup = node.value;
        if (zoomGroup) {
            const getId = formattedView.dataView.dataSource.getId;
            const dataView: DV.DataView<ITEM> = {
                dataSource: new DV.ArrayDataSource(zoomGroup.items, getId),
                extractorGroups: formattedView.dataView.extractorGroups,
                sorters: [],
                defaultCompareItems: formattedView.dataView.defaultCompareItems,
            };
            return {
                nodeId: pivotedView.dataSource.getId(node),
                pathValues: zoomPathValues,
                formattedView: this.buildFormattedView(
                    dataView,
                    this.props.columnBuilders
                ),
            };
        } else {
            return undefined;
        }
    }
    private buildPivotExpandedZoom(
        formattedView: FormattedView<ITEM>,
        viewForPivot: DV.DataView<DV.Expando<ITEM>>,
        pivotedView: DV.DataView<
            DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
        >,
        pivotExtractorIds: Array<string>,
        node: DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
    ): PivotExpandedZoom<ITEM> | undefined {
        const zoomPathValues = this.getPathValues(
            node,
            (extractorId) => DV.getExtractor(viewForPivot, extractorId),
            pivotExtractorIds
        );
        const zoomGroup = node.value;
        if (zoomGroup) {
            const getId = viewForPivot.dataSource.getId;
            const dataView: DV.DataView<DV.Expando<ITEM>> = {
                dataSource: new DV.ArrayDataSource(zoomGroup.items, getId),
                extractorGroups: viewForPivot.extractorGroups,
                sorters: [],
                defaultCompareItems: viewForPivot.defaultCompareItems,
            };
            return {
                nodeId: pivotedView.dataSource.getId(node),
                pathValues: zoomPathValues,
                formattedView: this.buildFormattedView(
                    dataView,
                    this.props.columnBuilders
                ),
            };
        } else {
            return undefined;
        }
    }
    private handleRefresh(mode: DisplayMode<ITEM>): void {
        let zoomArg: ZoomCallbackArgument<ITEM> | undefined;
        switch (mode.type) {
            case "SIMPLE": {
                zoomArg = undefined;
                break;
            }
            case "PIVOT": {
                const zoom = mode.zoom;
                if (zoom) {
                    const zoomGroupNodeDesc = this.findNode(
                        zoom.nodeId,
                        mode.formattedView.dataView
                    );
                    if (zoomGroupNodeDesc === null) {
                        throw new Error("No node with id " + zoom.nodeId);
                    }
                    zoomArg = {
                        type: "Simple",
                        group: zoomGroupNodeDesc.value,
                    };
                } else {
                    zoomArg = undefined;
                }
                break;
            }
            case "PIVOT_EXPANDED": {
                const zoom = mode.zoom;
                if (zoom) {
                    const zoomGroupNodeDesc = this.findNode(
                        zoom.zoom.nodeId,
                        mode.formattedView.dataView
                    );
                    if (zoomGroupNodeDesc === null) {
                        throw new Error("No node with id " + zoom.zoom.nodeId);
                    }
                    zoomArg = {
                        type: "Expanded",
                        group: zoomGroupNodeDesc.value,
                    };
                } else {
                    zoomArg = undefined;
                }
                break;
            }
            default: {
                return assertNever(mode);
            }
        }
        this.handleZoom(zoomArg);
    }
    private handleZoom(zoomNode: ZoomCallbackArgument<ITEM>) {
        const { onZoomData } = this.props;
        if (onZoomData) {
            onZoomData(zoomNode);
        }
    }

    private exportToExcel(): void {
        const { mode } = this.state;
        switch (mode.type) {
            case "SIMPLE": {
                this.exportFormattedViewToExcel(
                    this.state.formattedView,
                    (item) => item,
                    this.props.newExportMetadata()
                );
                break;
            }
            case "PIVOT": {
                if (mode.zoom) {
                    this.exportFormattedViewToExcel(
                        mode.zoom.formattedView,
                        (item) => item,
                        this.props.newExportMetadata()
                    );
                } else {
                    this.exportPivotFormattedViewToExcel(
                        mode.formattedView,
                        this.props.newExportMetadata()
                    );
                }
                break;
            }
            case "PIVOT_EXPANDED": {
                if (mode.zoom) {
                    switch (mode.zoom.type) {
                        case "Simple": {
                            this.exportFormattedViewToExcel(
                                mode.zoom.zoom.formattedView,
                                (item) => item,
                                this.props.newExportMetadata()
                            );
                            break;
                        }
                        case "Expanded": {
                            this.exportFormattedViewToExcel(
                                mode.zoom.zoom.formattedView,
                                (expando) => expando.baseItem,
                                this.props.newExportMetadata()
                            );
                            break;
                        }
                        default: {
                            return assertNever(mode.zoom);
                        }
                    }
                } else {
                    this.exportPivotFormattedViewToExcel(
                        mode.formattedView,
                        this.props.newExportMetadata()
                    );
                }
                break;
            }
            default: {
                return assertNever(mode);
            }
        }
    }
    private exportFormattedViewToExcel<T>(
        formattedView: FormattedView<T>,
        getItem: (t: T) => ITEM,
        metadata: ExportMetadata
    ) {
        const { customExportTableToExcel } = this.props;
        const isColumnDisplayed = stateToIsColumnDisplayed<T>(
            this.state.displayedTableColumns
        );
        const displayedColumns = formattedView.columns.filter((column) =>
            isColumnDisplayed(column)
        );
        if (customExportTableToExcel) {
            customExportTableToExcel(formattedView, getItem, displayedColumns);
        } else {
            DV.exportToExcel(
                formattedView.dataView,
                () => true,
                displayedColumns.map((column) => {
                    return {
                        label: column.extractor.label,
                        getValue: (item: T) => column.getCopyableValue(item),
                    };
                }),
                "report",
                "xlsx",
                "Report",
                metadata
            );
        }
    }
    private exportPivotFormattedViewToExcel<T>(
        formattedView: FormattedView<DV.NodeDescriptor<DV.Group<T, any>>>,
        metadata: ExportMetadata
    ) {
        const isColumnDisplayed = stateToIsColumnDisplayed<
            DV.NodeDescriptor<DV.Group<T, any>>
        >(this.state.displayedPivotColumns);
        const displayedColumns = formattedView.columns.filter((column) =>
            isColumnDisplayed(column)
        );
        const exportColumns: Array<
            DV.ExportColumn<DV.NodeDescriptor<DV.Group<T, any>>>
        > = [];
        displayedColumns.forEach((column) => {
            // For the grouping column in pivot mode, we want one column per
            // extractor used for grouping
            if ((column.extractor as DV.GroupingDataExtractor<T>).extractors) {
                const groupingExtractor =
                    column.extractor as DV.GroupingDataExtractor<T>;
                groupingExtractor.extractors.forEach((extractor, i) => {
                    exportColumns.push({
                        label: extractor.label,
                        getValue: (
                            item: DV.NodeDescriptor<DV.Group<T, any>>
                        ) => {
                            if (item.ancestors.length >= i + 1) {
                                return extractor.asString(item.value.items[0]);
                            } else {
                                return undefined;
                            }
                        },
                    });
                });
            } else {
                exportColumns.push({
                    label: column.extractor.label,
                    getValue: (item: DV.NodeDescriptor<DV.Group<T, any>>) =>
                        column.getCopyableValue(item),
                });
            }
        });
        DV.exportToExcel(
            formattedView.dataView,
            (item) => item.ancestors.length > 0,
            exportColumns,
            "report",
            "xlsx",
            "Report",
            metadata
        );
    }
}

interface TableDataViewerProps<ITEM> {
    id: string;
    dataView: DV.DataView<ITEM>;
    expanders?: Array<DV.ExtractorExpander<any, any>>;
    availableAggregates?: AggregatesConfiguration<ITEM>;

    // Be careful with column builders, the item may be expanded.
    // That is why ColumnBuilder is typed with any instead of ITEM/Expando<ITEM>.
    columnBuilders?: { [extractorId: string]: ColumnBuilder<any, any> };
    groupColumnBuilder?: ColumnBuilder<
        DV.NodeDescriptor<DV.Group<any, any>>,
        any
    >;
    aggregateColumnBuilders?: {
        [extractorId: string]: AggregateColumnBuilder<
            DV.NodeDescriptor<DV.Group<any, any>>,
            any
        >;
    };

    formatPathValues?: <T>(
        extractorAndValue: Array<ExtractorAndPathValue<T>>
    ) => React.ReactNode;

    settings?: DataViewerSettings;
    defaultGroupingExtractorIds?: Array<string>;
    defaultAggregates?: AggregatesSpec;

    zoomGroupId?: string;

    toolbarClassName?: string;
    floatingToolbar?: boolean;
    tools?: {
        table: Array<ToolSpec | DataViewerToolSpec>;
        pivot: Array<ToolSpec | DataViewerToolSpec>;
        zoom: Array<ToolSpec | DataViewerToolSpec>;
    };
    tableMaxVisibleRows?: number;

    onSettingsChanged?: (settings: DataViewerSettings) => void;
    onZoomOut?: () => void;
    onZoomData?: (zoomNode: ZoomCallbackArgument<ITEM>) => void;

    customExportTableToExcel?: <T>(
        formattedView: FormattedView<T>,
        getItem: (t: T) => ITEM,
        displayedColumns: Array<SimpleColumn<T>>
    ) => void;
    newExportMetadata: () => ExportMetadata;
}

type ToolsMode =
    | {
          name: "table";
      }
    | {
          name: "pivot";
      }
    | {
          name: "zoom";
          pathValues: FormattedPathValues;
      };

interface TableDataViewerState<ITEM> {
    settings: DataViewerSettings;
    dialog: SettingsDialogState | undefined;
    formattedView: FormattedView<ITEM>;
    mode: DisplayMode<ITEM>;
    displayedTableColumns: { [colId: string]: boolean };
    displayedPivotColumns: { [colId: string]: boolean };
}

interface SettingsDialogState {
    type: "Settings";
}

interface DataViewerSettingsToolSpec {
    dataViewerSettings: boolean;
}

interface DataViewerAdjustViewToolSpec {
    dataViewerAdjustView: boolean;
}

interface DataViewerExportToolSpec {
    dataViewerExport: boolean;
}

interface DataViewerZoomToolSpec {
    dataViewerZoom: boolean;
}

type DataViewerToolSpec =
    | DataViewerSettingsToolSpec
    | DataViewerAdjustViewToolSpec
    | DataViewerExportToolSpec
    | DataViewerZoomToolSpec;

interface ExtractorAndPathValue<T> {
    extractor: DV.DataExtractor<T, any>;
    value: string;
}

interface FormattedPathValues {
    values: Array<string>;
    formatted: React.ReactNode;
}

type DisplayMode<ITEM> = SimpleMode | PivotMode<ITEM> | PivotExpandedMode<ITEM>;

interface SimpleMode {
    type: "SIMPLE";
}

interface PivotMode<ITEM> {
    type: "PIVOT";
    pivotExtractorIds: Array<string>;
    formattedView: FormattedView<DV.NodeDescriptor<DV.Group<ITEM, any>>>;
    zoom?: PivotZoom<ITEM>;
}

interface PivotExpandedMode<ITEM> {
    type: "PIVOT_EXPANDED";
    pivotExtractorIds: Array<string>;
    expandedDataView: DV.DataView<DV.Expando<ITEM>>;
    aggregateExtractorGroups: Array<
        DV.DataExtractorGroup<
            DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
        >
    >;
    formattedView: FormattedView<
        DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
    >;
    zoom?: MaybeExpandedPivotZoom<ITEM>;
}

interface PivotZoom<ITEM> {
    nodeId: string;
    pathValues: FormattedPathValues;
    formattedView: FormattedView<ITEM>;
}

interface PivotExpandedZoom<ITEM> {
    nodeId: string;
    pathValues: FormattedPathValues;
    formattedView: FormattedView<DV.Expando<ITEM>>;
}

type MaybeExpandedPivotZoom<ITEM> =
    | {
          type: "Simple";
          zoom: PivotZoom<ITEM>;
      }
    | {
          type: "Expanded";
          zoom: PivotExpandedZoom<ITEM>;
      };

type ZoomCallbackArgument<ITEM> =
    | {
          type: "Simple";
          group: DV.Group<ITEM, any>;
      }
    | {
          type: "Expanded";
          group: DV.Group<DV.Expando<ITEM>, any>;
      }
    | undefined;

type PivotData<ITEM> =
    | {
          type: "Simple";
          viewForPivot: DV.DataView<ITEM>;
          aggregateExtractorGroups: Array<
              DV.DataExtractorGroup<DV.NodeDescriptor<DV.Group<ITEM, any>>>
          >;
          pivotedView: DV.DataView<DV.NodeDescriptor<DV.Group<ITEM, any>>>;
      }
    | {
          type: "Expanded";
          viewForPivot: DV.DataView<DV.Expando<ITEM>>;
          aggregateExtractorGroups: Array<
              DV.DataExtractorGroup<
                  DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
              >
          >;
          pivotedView: DV.DataView<
              DV.NodeDescriptor<DV.Group<DV.Expando<ITEM>, any>>
          >;
      };

// That's not optimal so I leave it here.
function findGroupNodeByItemId<ITEM>(
    dataSource: DV.DataSource<DV.NodeDescriptor<DV.Group<ITEM, any>>>,
    id: string
) {
    const iter = dataSource.iter();
    let next = iter.next();
    let foundNode: DV.NodeDescriptor<DV.Group<ITEM, any>> | undefined;
    while (!foundNode && next) {
        if (next.value.id === id) {
            foundNode = next;
            break;
        }
        next = iter.next();
    }
    return foundNode;
}
export type {
    Display,
    Layout,
    SimpleColumn,
    FormattedView,
    ColumnBuilder,
    ColumnOptions,
    TableDataViewerProps,
    AggregateColumnBuilder,
    DataViewerToolSpec,
};
export {
    TableDataViewer,
    DEFAULT_COLUMN_BUILDER_ID,
    toColumn,
    sortColumns,
    DataViewerGroupingSelector,
    ToolBarTheme,
};
